import { authSelectors } from 'ducks/auth'
import { ProposalDataType } from 'opensolar-checkout'
import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import useRenderDialog from 'resources/proposalTemplates/proposalV2/useRenderDialog'
import useProposalData from '../proposalV2/useProposalData'
import { DialogPreviewName } from '../types'
import { useFormattedProposalSettings } from './useFormattedProposalSettings'

type ClassicProposalPreviewContextValue = {
  // When `false` (default / outside the classic-template preview), `FieldWithInlineDialogPreview`
  // is an inert pass-through and `InlineDialogPreviewButton` renders nothing. This keeps other
  // callers of `RenderInputs` (proposal-v2 template, project-level editor) unaffected.
  available: boolean
  error: string | undefined
  showDialog(name: DialogPreviewName): void
  previewIsDirty: boolean
  refresh: (() => Promise<void>) | undefined
  handleSelectPayment(id: number): void
  handleSelectSystem(uuid: string): void
  isLoading: boolean
  proposalData: ProposalDataType | undefined
}

export const ClassicProposalPreviewContext = createContext<ClassicProposalPreviewContextValue>({
  available: false,
  error: undefined,
  isLoading: false,
  handleSelectPayment: () => {},
  handleSelectSystem: () => {},
  showDialog: () => {},
  previewIsDirty: false,
  refresh: undefined,
  proposalData: undefined,
})

type ProviderProps = {
  projectId: number | undefined
  children: ReactNode
}

export const ClassicProposalPreviewContextProvider = ({ projectId, children }: ProviderProps) => {
  const { error, data, isLoading, loadProposalData, handleSelectPayment, handleSelectSystem } = useProposalData()
  const [previewIsDirty, setPreviewIsDirty] = useState<boolean>(false)
  const form = useForm()
  const orgId = useSelector(authSelectors.getOrgId)
  const { formattedSettings } = useFormattedProposalSettings()
  const refreshRef = useRef<() => Promise<void>>()
  const { dialog, showDialog } = useRenderDialog({ proposalData: data })

  useEffect(() => {
    // set preview as dirty whenever form updated
    // Aware edge case when first select a project - acceptable
    setPreviewIsDirty(true)
  }, [formattedSettings])

  const refresh = useCallback(async () => {
    if (!projectId) return
    setPreviewIsDirty(false)
    await loadProposalData({
      id: projectId,
      unsavedData: {
        proposal_template: form.getState().values.url,
        proposal_template_settings_override: formattedSettings,
        org_id: orgId,
      },
    })
  }, [projectId, orgId, loadProposalData, formattedSettings])
  refreshRef.current = refresh

  // Load when the project is selected or switched
  useEffect(() => {
    // Avoid auto calling refresh when reference function updated
    if (projectId) refreshRef.current?.()
  }, [projectId])

  const available = !!projectId

  const value = useMemo<ClassicProposalPreviewContextValue>(
    () => ({
      error,
      proposalData: data as ProposalDataType | undefined,
      available,
      showDialog,
      previewIsDirty,
      refresh,
      handleSelectPayment,
      handleSelectSystem,
      isLoading,
    }),
    [error, available, showDialog, previewIsDirty, isLoading, refresh, data, handleSelectPayment, handleSelectSystem]
  )

  return (
    <ClassicProposalPreviewContext.Provider value={value}>
      {children}
      {dialog}
    </ClassicProposalPreviewContext.Provider>
  )
}
